import {
    WaitingListEntry,
    WAITING_LIST_STATUS_LABELS,
    WAITING_LIST_STATUS_COLORS,
} from '@/lib/api/waiting-list/waiting-list-api'
import { formatDate } from '../../utils/calendar-utils'
import {
    MdPerson,
    MdSchedule,
    MdNotifications,
    MdEdit,
    MdDelete,
    MdEventAvailable,
    MdPriorityHigh,
} from 'react-icons/md'

interface WaitingListCardProps {
    entry: WaitingListEntry
    onSchedule?: (entry: WaitingListEntry) => void
    onNotify?: (entry: WaitingListEntry) => void
    onEdit?: (entry: WaitingListEntry) => void
    onDelete?: (entry: WaitingListEntry) => void
}

export function WaitingListCard({
    entry,
    onSchedule,
    onNotify,
    onEdit,
    onDelete,
}: WaitingListCardProps) {
    const colors = WAITING_LIST_STATUS_COLORS[entry.status]
    const statusLabel = WAITING_LIST_STATUS_LABELS[entry.status]
    const createdAt = new Date(entry.createdAt)

    const handleSchedule = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onSchedule) {
            onSchedule(entry)
        }
    }

    const handleNotify = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onNotify) {
            onNotify(entry)
        }
    }

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onEdit) {
            onEdit(entry)
        }
    }

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onDelete) {
            onDelete(entry)
        }
    }

    const getPriorityColor = (priority: number) => {
        if (priority >= 8) return 'text-error'
        if (priority >= 5) return 'text-warning'
        return 'text-base-content/50'
    }

    return (
        <div
            className={`p-3 rounded-lg bg-base-100 border ${colors.border} border-l-4`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    {entry.client && (
                        <div className="flex items-center gap-2">
                            <MdPerson className="text-base-content/60" />
                            <span className="font-medium text-sm truncate">
                                {entry.client.name}
                            </span>
                        </div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                        <span
                            className={`px-2 py-0.5 rounded-full text-xs ${colors.bg} ${colors.text}`}
                        >
                            {statusLabel}
                        </span>
                        {entry.priority > 0 && (
                            <div
                                className={`flex items-center gap-1 text-xs ${getPriorityColor(
                                    entry.priority
                                )}`}
                            >
                                <MdPriorityHigh />
                                P{entry.priority}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {entry.notes && (
                <p className="text-xs text-base-content/60 mt-2 line-clamp-2">
                    {entry.notes}
                </p>
            )}

            <div className="flex items-center gap-1 mt-2 text-xs text-base-content/50">
                <MdSchedule />
                <span>Desde {formatDate(createdAt, 'dd/MM/yyyy')}</span>
            </div>

            {entry.status === 'WAITING' && (
                <div className="flex items-center gap-1 mt-3 pt-2 border-t border-base-300/30">
                    {onSchedule && (
                        <button
                            className="btn btn-xs btn-secondary flex-1"
                            onClick={handleSchedule}
                            title="Agendar"
                        >
                            <MdEventAvailable className="text-sm" />
                            Agendar
                        </button>
                    )}
                    {onNotify && (
                        <button
                            className="btn btn-xs btn-outline"
                            onClick={handleNotify}
                            title="Notificar"
                        >
                            <MdNotifications className="text-sm" />
                        </button>
                    )}
                    {onEdit && (
                        <button
                            className="btn btn-xs btn-ghost"
                            onClick={handleEdit}
                            title="Editar"
                        >
                            <MdEdit className="text-sm" />
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="btn btn-xs btn-ghost text-error"
                            onClick={handleDelete}
                            title="Remover"
                        >
                            <MdDelete className="text-sm" />
                        </button>
                    )}
                </div>
            )}

            {entry.status !== 'WAITING' && (onEdit || onDelete) && (
                <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-base-300/30">
                    {onEdit && (
                        <button
                            className="btn btn-xs btn-ghost"
                            onClick={handleEdit}
                            title="Editar"
                        >
                            <MdEdit className="text-sm" />
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="btn btn-xs btn-ghost text-error"
                            onClick={handleDelete}
                            title="Remover"
                        >
                            <MdDelete className="text-sm" />
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}
